'use client';

import ImageOptimizer from '@/components/image-optimizer';
import ImageViewer from '@/components/image-viewer';
import { STATUS_CODE } from '@/constants';
import { ApiError } from '@/services/base-api';
import {
  useReviewBlock,
  useReviewDelete,
  useReviewLike,
} from '@/services/mutation/review.mutation';
import {
  PlusReviewItem as PlusReviewItemType,
  PlusReviewReplyItem,
} from '@/services/types/review.type';
import { useMemberStore } from '@/stores/member.store';
import { useReviewInfoStore } from '@/stores/review.store';
import { useParams, useRouter } from 'next/navigation';
import { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import ReviewDialog from '../../../components/review-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger } from './select';

/** 삭제 완료 후 이동할 경로 (예: 학원 상세 or 리뷰 목록) */
type DeleteReturnPath = 'academy-detail' | 'review-list';

interface PlusReviewItemProps {
  academyId: string;
  review: PlusReviewItemType;
  isLast: boolean;
  updateReview: (updatedReview: PlusReviewItemType) => void;
  academyName?: string;
  /** 삭제 완료 후 어떤 페이지로 이동할지 (academy-detail: 학원 상세, review-list: 리뷰 목록) */
  deleteReturnTo?: DeleteReturnPath;
}

type ReviewMenuType =
  'block_clear' | 'webview_report' | 'block_request' | 'edit' | 'delete';

interface ReviewMenuItem {
  type: ReviewMenuType;
  title: string;
}

export default function PlusReviewItem({
  academyId,
  review,
  updateReview,
  isLast = false,
  academyName,
  deleteReturnTo = 'review-list',
}: PlusReviewItemProps) {
  const router = useRouter();
  const params = useParams();
  // review-list 페이지는 `[id]/[name]/review-list`라 URL에서 학원명을 읽을 수 있다.
  // (상세에서 렌더될 땐 academyName prop을 쓰므로 이 값은 폴백 용도.)
  const routeName = (params?.name as string) || '';

  const hash = useMemberStore((s) => s.hash);

  const deleteReviewMutation = useReviewDelete(review.id);
  const likeReviewMutation = useReviewLike(review.id);
  const blockReviewMutation = useReviewBlock(review.id);

  const images = Array.isArray(review.images) ? review.images : [];
  const replyRefs = useRef<{ [key: number]: HTMLSpanElement | null }>({});
  const [expandedReplies, setExpandedReplies] = useState<{
    [key: number]: boolean;
  }>({});
  const [shouldShowMore, setShouldShowMore] = useState<{
    [key: number]: boolean;
  }>({});
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [dialogType, setDialogType] = useState<
    'delete' | 'error' | 'cancel' | 'block_request' | 'block_clear'
  >('delete');
  const [dialogTitle, setDialogTitle] = useState<string>('');
  const [dialogMessage, setDialogMessage] = useState<string>('');
  const [dialogConfirmText, setDialogConfirmText] = useState<string>('확인');
  const [dialogCancelText, setDialogCancelText] = useState<string>('취소');
  const [isShowCancelButton, setIsShowCancelButton] = useState<boolean>(false);
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const getMenuItems = (): ReviewMenuItem[] => {
    if (review.modifiable) {
      return [
        { type: 'edit', title: '수정하기' },
        { type: 'delete', title: '삭제하기' },
      ];
    }

    if (!review.moreMenus) {
      return [];
    }

    const menuTypes = review.moreMenus.split('|') as ReviewMenuType[];
    const menuMap: Record<ReviewMenuType, string> = {
      block_clear: '차단해제하기',
      block_request: '차단하기',
      webview_report: '리뷰 신고하기',
      edit: '수정하기',
      delete: '삭제하기',
    };

    return menuTypes
      .filter((type) => menuMap[type])
      .map((type) => ({
        type,
        title: menuMap[type],
      }));
  };

  const handleMenuClick = (type: ReviewMenuType) => {
    console.log('메뉴 클릭', type, review.id);
    if (type === 'edit') {
      console.log('수정하기 클릭');
      useReviewInfoStore.setState({
        isEdit: true,
        reviewId: review.id,
        point: review.point,
        author: review.author,
        content: review.content,
        images: review.images,
      });
      router.push(`/academy/${academyId}/review-write`);
      return;
    } else if (type === 'delete') {
      console.log('삭제하기 클릭');
      setDialogTitle('리뷰를 삭제하시겠어요?');
      setDialogMessage('');
      setDialogConfirmText('삭제');
      setDialogCancelText('취소');
      setIsShowCancelButton(true);
      setDialogType('delete');
      setShowDialog(true);
      return;
    } else if (type === 'block_request') {
      setDialogTitle('리뷰를 차단하시겠어요?');
      setDialogMessage('차단할 경우 이 작성자의 모든 리뷰가 보이지 않습니다.');
      setDialogConfirmText('차단');
      setDialogCancelText('취소');
      setIsShowCancelButton(true);
      setDialogType('block_request');
      setShowDialog(true);
      return;
    } else if (type === 'block_clear') {
      setDialogTitle('리뷰 차단을 해제하시겠어요?');
      setDialogMessage(
        '차단해제할 경우 이 작성자의 모든 리뷰가 보이게 됩니다.',
      );
      setDialogConfirmText('차단해제');
      setDialogCancelText('취소');
      setIsShowCancelButton(true);
      setDialogType('block_clear');
      setShowDialog(true);
      return;
    } else if (type === 'webview_report') {
      router.push(`/academy/${academyId}/review-report?id=${review.id}`);
      return;
    }
  };

  // 응답 데이터 처리 및 리뷰 업데이트 공통 함수
  const handleReviewResponse = (data: PlusReviewItemType | unknown) => {
    console.log('handleReviewResponse', JSON.stringify(data));

    // API 응답 데이터를 ReviewItem 타입에 맞게 변환
    if (!data || typeof data !== 'object') {
      console.error('응답 데이터가 올바르지 않습니다.', data);
      return;
    }

    const rawData = data as Record<string, unknown>;

    // id를 문자열에서 숫자로 변환
    const id =
      typeof rawData.id === 'string'
        ? parseInt(rawData.id, 10)
        : typeof rawData.id === 'number'
          ? rawData.id
          : null;

    // modifiable을 숫자(1/0)에서 boolean으로 변환
    const modifiable =
      typeof rawData.modifiable === 'number'
        ? rawData.modifiable === 1
        : typeof rawData.modifiable === 'boolean'
          ? rawData.modifiable
          : false;

    // images 배열 변환 (필요한 필드만 추출)
    const images = Array.isArray(rawData.images)
      ? rawData.images
          .map((img: unknown) => {
            if (img && typeof img === 'object') {
              const imgData = img as Record<string, unknown>;
              return {
                id: typeof imgData.id === 'number' ? imgData.id : 0,
                width: typeof imgData.width === 'number' ? imgData.width : 0,
                height: typeof imgData.height === 'number' ? imgData.height : 0,
                savePath:
                  typeof imgData.savePath === 'string' ? imgData.savePath : '',
                thumbWidth:
                  typeof imgData.thumbWidth === 'number'
                    ? imgData.thumbWidth
                    : 0,
                thumbHeight:
                  typeof imgData.thumbHeight === 'number'
                    ? imgData.thumbHeight
                    : 0,
                thumbSavePath:
                  typeof imgData.thumbSavePath === 'string'
                    ? imgData.thumbSavePath
                    : '',
              };
            }
            return null;
          })
          .filter((img): img is PlusReviewItemType['images'][0] => img !== null)
      : [];

    // reply 배열 변환 (있는 경우)
    const reply: PlusReviewReplyItem[] | undefined = Array.isArray(
      rawData.reply,
    )
      ? rawData.reply
          .map((r: unknown) => {
            if (r && typeof r === 'object') {
              const replyData = r as Record<string, unknown>;
              return {
                author:
                  typeof replyData.author === 'string' ? replyData.author : '',
                content:
                  typeof replyData.content === 'string'
                    ? replyData.content
                    : '',
                date: typeof replyData.date === 'string' ? replyData.date : '',
              };
            }
            return null;
          })
          .filter((r): r is PlusReviewReplyItem => r !== null)
      : undefined;

    const updatedReviewData: PlusReviewItemType = {
      id: id ?? 0,
      point: typeof rawData.point === 'number' ? rawData.point : 0,
      author: typeof rawData.author === 'string' ? rawData.author : '',
      date: typeof rawData.date === 'string' ? rawData.date : '',
      content: typeof rawData.content === 'string' ? rawData.content : '',
      moreMenus: typeof rawData.moreMenus === 'string' ? rawData.moreMenus : '',
      numLike: typeof rawData.numLike === 'number' ? rawData.numLike : 0,
      isLike: typeof rawData.isLike === 'boolean' ? rawData.isLike : false,
      modifiable,
      images,
      ...(reply && reply.length > 0 ? { reply } : {}),
    };

    console.log(
      'updatedReviewData',
      JSON.stringify(updatedReviewData),
      updatedReviewData.id,
      review.id,
      updatedReviewData.id === review.id,
    );

    if (updatedReviewData.id && updatedReviewData.id === review.id) {
      updateReview(updatedReviewData);
    } else {
      console.error('응답에서 리뷰 정보를 찾을 수 없습니다.', data);
    }
  };

  const handleLikeClick = () => {
    console.log('좋아요 클릭', review.id, !review.isLike);

    //alert(`likeReviewMutation : ${JSON.stringify(likeReviewMutation)}`);
    //alert(`handleLikeClick : ${hash}`);

    likeReviewMutation.mutateAsync(
      {
        reviewId: review.id,
        params: {
          hash: hash ?? '',
        },
      },
      {
        onSuccess: (data) => {
          console.log('likeReviewMutation onSuccess', JSON.stringify(data));
          if (data && data.code === STATUS_CODE.OK && data.data) {
            handleReviewResponse(data.data);
          } else {
            setDialogTitle(data.message);
            setDialogMessage('');
            setDialogConfirmText('확인');
            setDialogCancelText('취소');
            setIsShowCancelButton(false);
            setDialogType('error');
            setShowDialog(true);
          }
        },
        onError: (error: Error) => {
          //console.log('error', error);
          setDialogTitle(error.message);
          setDialogMessage('');
          setDialogConfirmText('확인');
          setIsShowCancelButton(true);
          setDialogType('error');
          setShowDialog(true);
        },
      },
    );
  };

  const handleDialogConfirm = () => {
    if (dialogType === 'delete' && isShowCancelButton) {
      // 리뷰 삭제 확인 다이얼로그에서 확인 클릭 시 (취소 버튼이 있는 경우)
      console.log('리뷰 삭제 확인', review.id);
      deleteReviewMutation.mutateAsync(
        {
          reviewId: review.id,
          params: {
            hash: hash ?? '',
            academyId: academyId,
          },
        },
        {
          onSuccess: (data) => {
            // console.log('data', data);
            if (data && data.code === STATUS_CODE.OK && data.data) {
              setDialogTitle(data.data.message);
              setDialogMessage('');
              setDialogConfirmText('확인');
              setDialogCancelText('취소');
              setIsShowCancelButton(false);
              setDialogType('delete');
              setShowDialog(true);
            } else {
              // toast(`${data.data.message}`, {
              //   duration: 2000,
              // });
              setDialogTitle(data.data.message);
              setDialogMessage('');
              setDialogConfirmText('확인');
              setDialogCancelText('취소');
              setIsShowCancelButton(false);
              setDialogType('error');
              setShowDialog(true);
            }
          },
          onError: (error: Error) => {
            //console.log('error', error);
            const apiError = error as unknown as ApiError;
            setDialogTitle(apiError?.message || '리뷰 삭제에 실패했습니다.');
            setDialogMessage('');
            setDialogConfirmText('확인');
            setDialogCancelText('취소');
            setIsShowCancelButton(false);
            setDialogType('error');
            setShowDialog(true);
          },
        },
      );
    } else if (dialogType === 'delete' && !isShowCancelButton) {
      // 삭제 완료 다이얼로그에서 확인 클릭 시 (취소 버튼이 없는 경우)
      setShowDialog(false);
      if (deleteReturnTo === 'academy-detail' && academyName) {
        const path = `/academy/${academyId}/${encodeURIComponent(academyName)}?refresh=${Date.now()}`;
        router.replace(path);
      } else {
        // review-list: 리뷰 목록 페이지로 이동 (refresh로 새로고침).
        // 학원명을 알면 새 URL(`[id]/[name]/review-list`)로 직접 이동해
        // 구 URL 리다이렉트 hop을 피한다. 모르면 구 URL(리다이렉터가 처리).
        const nameForPath = academyName || routeName;
        const path = nameForPath
          ? `/academy/${academyId}/${encodeURIComponent(nameForPath)}/review-list?refresh=${Date.now()}`
          : `/academy/${academyId}/review-list?refresh=${Date.now()}`;
        router.replace(path);
      }
    } else if (dialogType === 'error') {
      // 에러 다이얼로그에서 확인 클릭 시
      setShowDialog(false);
    } else if (dialogType === 'block_request') {
      console.log('리뷰 차단 확인', review.id);
      blockReviewMutation.mutateAsync(
        {
          reviewId: review.id,
          params: {
            hash: hash ?? '',
            status: 'block_request',
          },
        },
        {
          onSuccess: (data) => {
            // console.log(
            //   'blockReviewMutation block_request onSuccess',
            //   JSON.stringify(data),
            // );
            if (data && data.code === STATUS_CODE.OK && data.data) {
              handleReviewResponse(data.data);
            } else {
              toast(`${data.message}`, {
                duration: 2000,
              });
            }
            setShowDialog(false);
          },
          onError: (error: Error) => {
            console.log('error', error);
            setShowDialog(false);
            toast(`${error.message}`, {
              duration: 2000,
            });
          },
        },
      );
    } else if (dialogType === 'block_clear') {
      setShowDialog(false);
      blockReviewMutation.mutateAsync(
        {
          reviewId: review.id,
          params: {
            hash: hash ?? '',
            status: 'block_clear',
          },
        },
        {
          onSuccess: (data) => {
            console.log(
              'blockReviewMutation block_clear onSuccess',
              JSON.stringify(data),
            );
            if (data && data.code === STATUS_CODE.OK && data.data) {
              handleReviewResponse(data.data);
            } else {
              toast(`${data.message}`, {
                duration: 2000,
              });
            }
          },
          onError: (error: Error) => {
            console.log('error', error);
            setShowDialog(false);
            toast(`${error.message}`, {
              duration: 2000,
            });
          },
        },
      );
    } else {
      setShowDialog(false);
    }
  };

  const handleDialogCancel = () => {
    // 삭제 확인 다이얼로그에서 취소 클릭 시
    setShowDialog(false);
  };

  const renderStars = () => {
    return Array.from({ length: 5 }, (_, index) => {
      const fillPercentage = Math.max(0, Math.min(1, review.point - index));

      return (
        <div key={index} className="relative" style={{ width: 16, height: 16 }}>
          {/* 회색 배경 별 */}
          <div className="absolute inset-0">
            <ImageOptimizer
              src="/images/academy/icon_star_off.svg"
              alt="별점 배경"
              width={16}
              height={16}
            />
          </div>
          {/* 노란색 별 (비율에 따라 좌측에서만 클리핑) */}
          {fillPercentage > 0 && (
            <div
              className="absolute left-0 top-0 overflow-hidden"
              style={{
                width: `${fillPercentage * 100}%`,
                height: '100%',
              }}
            >
              <div
                className="absolute left-0 top-0"
                style={{ width: 16, height: 16 }}
              >
                <ImageOptimizer
                  src="/images/academy/icon_star_on.svg"
                  alt="별점"
                  width={16}
                  height={16}
                  style={{ display: 'block', width: 16, height: 16 }}
                />
              </div>
            </div>
          )}
        </div>
      );
    });
  };

  const menuItems = getMenuItems();

  // 댓글 길이 체크 함수
  const checkReplyLength = (element: HTMLElement | null, index: number) => {
    if (!element) return;

    // 이미 더보기 버튼이 표시되어 있으면 체크하지 않음
    if (shouldShowMore[index]) return;

    // 텍스트 내용 가져오기 (HTML 태그 제거)
    const textContent = element.textContent || element.innerText || '';
    const textLength = textContent.trim().length;

    // 70자 이상이면 더보기 버튼 표시
    if (textLength >= 70) {
      setShouldShowMore((prev) => {
        if (prev[index]) return prev; // 이미 설정되어 있으면 변경하지 않음
        return { ...prev, [index]: true };
      });
    }
  };

  const toggleReply = (index: number) => {
    setExpandedReplies((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  return (
    <div>
      <div className="p-5 bg-white">
        <div className="flex items-start justify-between mb-[14px]">
          <div>
            <div className="mb-[7px]">
              <span className="typo-15 text-[#222222] font-bold">
                {review.author ?? ''}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center" style={{ gap: '1px' }}>
                {renderStars()}
              </div>
              <div className="flex items-center gap-[5px]">
                <span className="typo-14 font-extrabold text-[#222222]">
                  {review.point}
                </span>
              </div>
            </div>
          </div>
          {menuItems.length > 0 && (
            <Select>
              <SelectTrigger className="w-[14px] h-[14px] p-0 border-0 bg-transparent cursor-pointer mt-1 flex items-center justify-center [&>*:last-child]:hidden focus:ring-0 focus:ring-offset-0">
                <ImageOptimizer
                  src="/images/academy/review/icon_more.svg"
                  alt="더보기"
                  width={2}
                  height={14}
                />
              </SelectTrigger>
              <SelectContent
                className="bg-white rounded border border-[#D6D4E2] z-[9999] [&>div[data-radix-select-viewport]]:p-0"
                side="bottom"
                align="end"
                sideOffset={0}
                collisionPadding={8}
              >
                {menuItems.map((item, index) => (
                  <div
                    key={item.type}
                    onClick={() => handleMenuClick(item.type)}
                  >
                    <SelectItem
                      value={item.type}
                      className="cursor-pointer rounded-none px-4.5 py-3 outline-none focus:bg-transparent hover:bg-transparent focus:text-[#222222] [&>span:first-child]:hidden"
                    >
                      <span className="typo-15 text-[#222222] font-semibold">
                        {item.title}
                      </span>
                    </SelectItem>
                    {index < menuItems.length - 1 && (
                      <div className="h-[1px] bg-[#D6D4E2]" />
                    )}
                  </div>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <div className="mb-4">
          <span
            className={`typo-14 font-medium leading-[14px] ${review.moreMenus.includes('block_clear') ? 'text-[#908E9E]' : 'text-[#333333]'}`}
          >
            {review.content ?? ''}
          </span>
        </div>

        {images.length > 0 && (
          <div className="flex gap-1 mb-4">
            {images
              .filter(
                (image) =>
                  (image.thumbSavePath && image.thumbSavePath.trim() !== '') ||
                  (image.savePath && image.savePath.trim() !== ''),
              )
              .map((image, index) => (
                <div
                  key={index}
                  className="relative cursor-pointer"
                  style={{ width: 60, height: 60, flexShrink: 0 }}
                  onClick={() => {
                    setSelectedImageIndex(index);
                    setImageViewerOpen(true);
                  }}
                >
                  <ImageOptimizer
                    src={image.thumbSavePath ?? image.savePath}
                    alt={`리뷰 이미지 ${index + 1}`}
                    width={60}
                    height={60}
                    className="rounded"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                </div>
              ))}
          </div>
        )}

        <div className="flex items-center justify-between">
          <div className="typo-12 font-medium text-[#908E9E]">
            {review.date.replaceAll('-', '.')}
          </div>
          <div className="flex items-center gap-2">
            <span className="typo-12 font-semibold text-[#333333]">
              {`${review.numLike}명에게 도움이 됐어요`}
            </span>
            {!review.modifiable && (
              <button
                type="button"
                onClick={handleLikeClick}
                className="flex items-center justify-center cursor-pointer"
                aria-label={review.isLike ? '좋아요 취소' : '좋아요'}
              >
                <ImageOptimizer
                  src={
                    review.isLike
                      ? '/images/academy/review/icon_like_on.svg'
                      : '/images/academy/review/icon_like_off.svg'
                  }
                  alt={review.isLike ? '좋아요' : '좋아요 취소'}
                  width={23}
                  height={23}
                />
              </button>
            )}
          </div>
        </div>
      </div>
      {review.reply &&
        review.reply.length > 0 &&
        review.reply.map((reply, replyIndex) => {
          const replyDate = reply.date.split(' ')[0].replaceAll('-', '.');
          const replyContent = reply.content.replaceAll('\n', '<br />');
          const isExpanded = expandedReplies[replyIndex] || false;
          const showMore = shouldShowMore[replyIndex] || false;

          return (
            <div key={replyIndex} className="pb-5 bg-white">
              <div className="relative px-5">
                {/* 말풍선 왼쪽 위 삼각형 포인터 */}
                <div
                  className="absolute top-[-8px] left-5"
                  style={{
                    width: 0,
                    height: 0,
                    borderRight: '8px solid transparent',
                    borderBottom: '8px solid #F5F5FA',
                  }}
                />
                <div className="p-4 bg-[#F5F5FA] rounded-tr-[8px] rounded-bl-[8px] rounded-br-[8px]">
                  <span className="typo-14 text-[#222222] font-bold flex items-center gap-1 mb-3">
                    {reply.author ?? '무명'}
                    <span className="typo-12 text-[#908E9E] font-medium">
                      {` | ${replyDate}`}
                    </span>
                  </span>
                  <div className="relative">
                    <span
                      ref={(el) => {
                        replyRefs.current[replyIndex] = el;
                        if (el) {
                          checkReplyLength(el, replyIndex);
                        }
                      }}
                      className={`typo-14 text-[#333333] font-medium ${
                        showMore && !isExpanded
                          ? 'line-clamp-2 overflow-hidden'
                          : 'block'
                      }`}
                      style={{
                        lineHeight: '1.4',
                      }}
                    >
                      <span
                        dangerouslySetInnerHTML={{ __html: replyContent }}
                      />
                    </span>
                    {showMore && (
                      <div className="mt-1">
                        <button
                          type="button"
                          onClick={() => toggleReply(replyIndex)}
                          className="typo-14 text-[#908E9E] font-medium cursor-pointer outline-none focus:outline-none active:outline-none"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          {isExpanded ? '접기' : '더보기'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      {!isLast && <div className="h-[1px] bg-[#D6D4E2]" />}
      <ReviewDialog
        open={showDialog}
        isShowCancelButton={isShowCancelButton}
        confirmText={dialogConfirmText}
        cancelText={dialogCancelText}
        onConfirm={handleDialogConfirm}
        onCancel={handleDialogCancel}
        title={dialogTitle}
        message={dialogMessage}
      />
      <ImageViewer
        key={`image-viewer-${selectedImageIndex}`}
        images={images.filter(
          (image) =>
            (image.thumbSavePath && image.thumbSavePath.trim() !== '') ||
            (image.savePath && image.savePath.trim() !== ''),
        )}
        initialIndex={selectedImageIndex}
        open={imageViewerOpen}
        onClose={() => setImageViewerOpen(false)}
      />
    </div>
  );
}
